import { ceil } from "@suse-o11y/ceil";
import { differenceInMilliseconds, getTime, secondsToMilliseconds, subMilliseconds } from "@suse-o11y/date-time";
import type { Time } from "@suse-o11y/date-time/time";
import { max } from "@suse-o11y/max";
import { type UseInfiniteQueryResult, useQueryClient } from "@suse-o11y/query";
import type { Record } from "@suse-o11y/record";
import { split } from "@suse-o11y/split";
import isEmpty from "lodash/isEmpty";
import omit from "lodash/omit";
import { useCallback } from "react";

import { SpecificRange, type TimeRange } from "../app/state/view/timeline/TimeRange";
import {
	type ComponentQuery,
	type DurationHistogram,
	type SpanComponents,
	type SpanFilter,
	type SpanResponse,
	type SpanSortOption,
	type TraceQueryResponse,
	getSpanFn,
	queryDurationFn,
	queryTracesFn,
	spanComponentsFn,
	suggestionsAttributeNameFn,
	suggestionsAttributeValueFn,
} from "../common/openapi";

import {
	type UseFullQueryOptions,
	type UseInfiniteQueryOptions,
	type UseQueryOptions,
	type UseQueryResult,
	useInfiniteQuery,
	useQuery,
} from "./react-query";

/**
 * The querySpansFn http responses are likely to differ for the same request payload.
 * It is due to the fact that the server-side spans are being updated in real-time and the response is not cached.
 * So the spans might arrive later than their start time.
 *
 * To avoid missing spans, the time range for the spans page request is shifted by a maximum of 30 seconds from now.
 */
const SPANS_TIME_RANGE_MAX_SHIFT_MS = secondsToMilliseconds(30);

const shiftSpansTimeRange = (timeRange: SpecificRange): SpecificRange => {
	const diffFormNow = differenceInMilliseconds(Date.now(), timeRange.endDate());
	// shift the time range by a maximum of 30 seconds from now
	const shift = max(0, SPANS_TIME_RANGE_MAX_SHIFT_MS - diffFormNow);

	if (shift === 0) {
		return timeRange;
	}

	return new SpecificRange(subMilliseconds(timeRange.start(), shift), subMilliseconds(timeRange.endDate(), shift));
};

export interface InfiniteTraces extends TraceQueryResponse {
	requestRange: SpecificRange;
}

interface TracesPageParam {
	page: number;
	requestRange: SpecificRange;
}

export interface UseInfinitiveQueryTracesParams {
	timeRange: TimeRange;
	spanFilter: SpanFilter;

	/**
	 * attributes that select a span from the trace, not to be edited/shown to the user
	 */
	traceAttributes: Record<string, Array<string>>;
	sortBy: Array<SpanSortOption>;
	pageSize: number;
}

export const useInfinitiveQueryTraces = <TResult = InfiniteTraces>(
	{ timeRange, spanFilter, traceAttributes, sortBy, pageSize }: UseInfinitiveQueryTracesParams,
	options?: UseInfiniteQueryOptions<TracesPageParam, InfiniteTraces, Error, TResult>,
): UseInfiniteQueryResult<TResult, Error> => {
	return useInfiniteQuery({
		queryKey: ["traces", "spans", { spanFilter, traceAttributes, sortBy, pageSize }],
		queryFn: async ({
			pageParam = {
				page: 0,
				requestRange: shiftSpansTimeRange(timeRange.toSpecific()),
			},
		}) => {
			const spans = await queryTracesFn({
				page: pageParam.page,
				start: getTime(pageParam.requestRange.start()),
				end: getTime(pageParam.requestRange.endDate()),
				pageSize,
				body: {
					primarySpanFilter: isEmpty(spanFilter.attributes) ? omit(spanFilter, "attributes") : spanFilter,
					secondarySpanFilter: {
						attributes: traceAttributes,
					},
					sortBy,
				},
			});

			return {
				...spans,
				requestRange: pageParam.requestRange,
			};
		},
		getNextPageParam: (lastPage) => {
			if (lastPage.matchesTotal < pageSize || lastPage.pageSize === 0) {
				return undefined;
			}

			const nextPage = lastPage.page + 1;
			return lastPage.page < ceil(lastPage.matchesTotal / lastPage.pageSize, 0)
				? /**
					 * it is essential to keep the same timeRange to request the next page(s)
					 * within the same time requestRange as the first page
					 */
					{
						page: nextPage,
						requestRange: lastPage.requestRange,
					}
				: undefined;
		},
		...options,
		timeRange,
	});
};

export const useQueryDurationHistogram = <TResult = DurationHistogram>(
	{
		timeRange,
		bucketsCount,
		spanFilter,
		traceAttributes,
	}: {
		timeRange: TimeRange;
		bucketsCount: number;
		spanFilter: SpanFilter;
		traceAttributes: Record<string, Array<string>>;
	},
	options: UseQueryOptions<DurationHistogram, Error, TResult> = {},
): UseQueryResult<TResult, Error> => {
	return useQuery(
		["traces", "spans", "histogram", bucketsCount, spanFilter, traceAttributes],
		() => {
			const shiftedTimeRange = shiftSpansTimeRange(timeRange.toSpecific());

			return queryDurationFn({
				start: getTime(shiftedTimeRange.start()),
				end: getTime(shiftedTimeRange.endDate()),
				bucketsCount,
				// durationFromNanos and durationToNanos should not be sent to the backend in histogram request, they have a fixed value for now (0-60e9ns)
				body: {
					primarySpanFilter: omit(spanFilter, ["durationFromNanos", "durationToNanos"]),
					secondarySpanFilter: {
						attributes: traceAttributes,
					},
				},
			});
		},
		{
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
			...options,
			timeRange,
		},
	);
};

export const useRemoveQuerySpans = (
	options: undefined | Omit<UseInfinitiveQueryTracesParams, "timeRange">,
): VoidFunction => {
	const queryClient = useQueryClient();
	const { spanFilter, traceAttributes, sortBy, pageSize } = options ?? {};

	return useCallback(() => {
		if (spanFilter != null && traceAttributes != null && sortBy != null && pageSize != null) {
			queryClient.removeQueries({
				queryKey: [{}, "traces", "spans", { spanFilter, traceAttributes, sortBy, pageSize }],
			});
		}
	}, [queryClient, spanFilter, traceAttributes, sortBy, pageSize]);
};

export const useQuerySpan = <TResult = SpanResponse>(
	traceId: string,
	spanId: string,
	options?: UseQueryOptions<SpanResponse, Error, TResult>,
): UseQueryResult<TResult, Error> =>
	useQuery(["traces", "trace", traceId, "spans", spanId], () => getSpanFn({ traceId, spanId }), options);

export const useSpanComponents = <TResult = SpanComponents>(
	query: Omit<ComponentQuery, "instant">,
	timestamp?: Time,
	options?: UseQueryOptions<SpanComponents, Error, TResult>,
): UseQueryResult<TResult, Error> =>
	useQuery(
		["traces", "components", query],
		() =>
			spanComponentsFn({
				body: {
					...query,
					instant: getTime(timestamp),
				},
			}),
		{
			timestamp,
			...options,
		},
	);

export const queryAttributeSuggestions = ((query: string) =>
	({
		queryKey: ["traces", "suggestions", query],
		queryFn: async () => {
			const [first, ...rest] = split(query, ":");

			if (rest.length === 0) {
				const { suggestions } = await suggestionsAttributeNameFn({ contains: first });

				return suggestions.map((value) => `${value}:`);
			}

			const { suggestions } = await suggestionsAttributeValueFn({
				attributeName: first,
				contains: rest.join(":"),
			});

			return suggestions.map((value) => `${first}:${value}`);
		},
	}) as const) satisfies (query: string) => UseFullQueryOptions;
